import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { Package, ChevronRight } from 'lucide-react';

function OrdersPage() {
  const { t } = useI18n();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    async function loadOrders() {
      try {
        const response = await api.get('/orders');
        if (mounted) {
          setOrders(response.data || []);
        }
      } catch (error) {
        console.error('Failed to load orders', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }
    
    loadOrders();
    
    return () => {
      mounted = false;
    };
  }, []);

  function getStatusColor(status) {
    const colors = {
      'eingegangen': 'bg-blue-100 text-blue-800',
      'in_bearbeitung': 'bg-yellow-100 text-yellow-800',
      'versendet': 'bg-purple-100 text-purple-800',
      'geliefert': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  function getStatusText(status) {
    const statusMap = {
      'new': t('status_new'),
      'eingegangen': t('status_received'),
      'in_bearbeitung': t('status_in_progress'),
      'versendet': t('status_shipped'),
      'geliefert': t('status_delivered')
    };
    return statusMap[status] || status;
  }

  function getPaymentStatusBadge(order) {
    if (order.payment_status === 'paid') {
      return <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">{t('paid')}</span>;
    } else if (order.payment_method === 'cash') {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium">{t('payOnDelivery')}</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">{t('pending')}</span>;
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 lg:px-12 py-8">
        <div className="text-center py-12">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Package className="h-8 w-8" />
        <h1 className="text-3xl md:text-4xl font-heading font-bold uppercase" data-testid="orders-title">
          {t('orders')}
        </h1>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          {t('noOrders')}
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order.id}
              className="bg-white border border-gray-200 rounded-md p-6"
              data-testid={`order-${order.id}`}
            >
              <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
                <div>
                  <p className="text-sm text-gray-500">
                    {t('orderNumber')} #{order.id ? order.id.slice(0, 8).toUpperCase() : 'N/A'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {order.created_at ? new Date(order.created_at).toLocaleDateString('de-DE') : ''}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {getPaymentStatusBadge(order)}
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                  </span>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                {(order.items || []).map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>
                      {item.product_name} x{item.quantity}
                    </span>
                    <span>€{(item.total || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 pt-4 flex flex-wrap justify-between items-center gap-4">
                <div>
                  <span className="font-semibold">{t('total')}: </span>
                  <span className="text-xl font-bold">€{(order.total || 0).toFixed(2)}</span>
                </div>
                
                <Link
                  to={`/orders/${order.id}`}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3"
                  data-testid={`view-order-${order.id}`}
                >
                  {t('downloadOrder')}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default OrdersPage;
